import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { SettingsDomain, SettingsResponse } from "../../shared/types";
import { Button } from "../components/ui/Button";
import { cx } from "../components/ui/cx";
import { Icon } from "../components/ui/Icon";
import { Select } from "../components/ui/Select";
import { Sheet } from "../components/ui/Sheet";
import { Toggle } from "../components/ui/Toggle";
import { useToast } from "../components/ui/Toast";
import { useAuth } from "../auth/AuthContext";
import { ApiError, api } from "../lib/api";
import { workspaceHome } from "../lib/workspace";
import { CopyValue, JoinInput, type RecState, RecStatus, useCopy, usePreviewWho } from "./parts";
import { BrowserFrame, PreviewFit, SignupPreview, usePreviewRoom } from "./Previews";
import { cleanDomain, isDomain, ownDomainGuess, rootOf, slugOk } from "./rules";
import { useSetup } from "./SetupContext";
import { BackButton, Sq, type StepProps } from "./SetupView";
import type { Run } from "./useSettingsData";

/** What the bare address opens, in words: the room picked, the newest one, or nothing yet. */
export function opensLabel(d: SettingsDomain): string {
  const chosen = d.published_webinars.find((w) => w.id === d.main_webinar_id);
  return chosen?.title ?? d.published_webinars[0]?.title ?? "your newest room, once you make one";
}

/** An example of an address the business owns, under its own domain when setup can tell what that is. */
export function ownExample(data: SettingsResponse, email?: string): string {
  return `rooms.${ownDomainGuess(data.sending.domain, email) ?? "yourbusiness.com"}`;
}

export type Avail = "ok" | "checking" | "taken" | "bad";

// --- 3. Your web address ---

/** The free address being typed: whether it is free, what to try if it is not, and the last one that was good. */
export function useSlugCheck(current: string) {
  const [slug, setSlug] = useState(current);
  const [avail, setAvail] = useState<Avail>("ok");
  const [suggestion, setSuggestion] = useState<string | null>(null);
  // A taken or unchecked name never reaches the preview: it keeps the last available one.
  const [good, setGood] = useState(current);

  useEffect(() => {
    if (slug === current) {
      setAvail("ok");
      setGood(slug);
      return;
    }
    setAvail("checking");
    let alive = true;
    const t = window.setTimeout(() => {
      if (!slugOk(slug)) return setAvail("bad");
      api.settings
        .addressAvailable(slug)
        .then((r) => {
          if (!alive) return;
          if (r.available || r.current) {
            setAvail("ok");
            setGood(slug);
          } else {
            setAvail("taken");
            setSuggestion(r.suggestion);
          }
        })
        .catch(() => alive && setAvail("bad"));
    }, 650);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [slug, current]);

  return { slug, setSlug, avail, suggestion, good };
}

/** Saving a new free address. The app lives under the business's address, so a new one opens the app there, at `then`. */
export function useSaveAddress(then: string) {
  const toast = useToast();
  return async (slug: string): Promise<void> => {
    try {
      const res = await api.settings.updateAddress({ subdomain: slug });
      window.location.assign(`${workspaceHome(res.subdomain)}${then}`);
    } catch (e) {
      toast({ text: e instanceof ApiError ? e.message : "Something went wrong.", icon: "info" });
    }
  };
}

/** Continuing from the address: a new free address is saved and opens the app there; the same one marks the step done. */
export function useAddressNext(data: SettingsResponse, slug: string, avail: Avail) {
  const navigate = useNavigate();
  const { set } = useSetup();
  const toast = useToast();
  const saveAddress = useSaveAddress("setup/tracking");
  const [busy, setBusy] = useState(false);
  const next = async () => {
    if (avail !== "ok") return;
    setBusy(true);
    try {
      if (slug !== data.domain.subdomain) {
        await saveAddress(slug);
        return;
      }
      set(await api.settings.markSetup({ step: "address" }));
      navigate("/setup/tracking");
    } catch (e) {
      toast({ text: e instanceof ApiError ? e.message : "Something went wrong.", icon: "info" });
    } finally {
      setBusy(false);
    }
  };
  return { busy, next };
}

export function AddressStep({ data, run }: StepProps) {
  const d = data.domain;
  const { slug, setSlug, avail, suggestion, good } = useSlugCheck(d.subdomain);
  const { busy, next } = useAddressNext(data, slug, avail);
  const room = usePreviewRoom(d.main_webinar_id);
  const who = usePreviewWho(data, room);

  const own = d.custom_domain_status === "verified" && d.custom_domain;
  const shown = own ? d.custom_domain ?? "" : `${good}.flashsuite.net`;

  return (
    <div className="sbody">
      <Sq
        eyebrow="Step 3 of 4 · Your web address"
        title="Where will people find your rooms?"
        foot={
          <>
            <BackButton to="/setup/email/test" />
            <span className="sp" />
            <Button variant="p" size="lg" after="right" disabled={avail !== "ok" || busy} onClick={() => void next()}>
              Continue
            </Button>
          </>
        }
      >
        <div className="field">
          <label htmlFor="slug">Your free address</label>
          <JoinInput id="slug" large value={slug} onChange={(v) => setSlug(v.toLowerCase())} suffix=".flashsuite.net" />
          <div aria-live="polite">
            {avail === "checking" ? (
              <span className="rst checking">
                <span className="spin" />
                Checking
              </span>
            ) : avail === "taken" ? (
              <span className="rst" style={{ color: "var(--bad)" }}>
                <Icon name="x" /> {slug} is taken.
                {suggestion ? (
                  <>
                    {" "}
                    Try{" "}
                    <button type="button" className="link" onClick={() => setSlug(suggestion)}>
                      {suggestion}
                    </button>
                  </>
                ) : null}
              </span>
            ) : avail === "bad" ? (
              <span className="rst" style={{ color: "var(--bad)" }}>
                <Icon name="x" /> Use letters, numbers and dashes
              </span>
            ) : (
              <RecStatus state="found" found="Available, it’s yours" />
            )}
          </div>
        </div>
        <OwnDomainCard data={data} run={run} freeHost={`${good}.flashsuite.net`} />
        <div className="field">
          <div className="lbl">When someone opens the address, they see</div>
          <OpensChoice data={data} run={run} />
        </div>
      </Sq>
      <div className="sstage">
        <div className="pvbar">
          <div className="stage-cap">
            <Icon name="globe" />{" "}
            <span>
              <b>{shown}</b>
            </span>
          </div>
          <CopyShown value={shown} />
        </div>
        <PreviewFit>
          <BrowserFrame url={shown}>
            <SignupPreview colours={data.brand} who={{ ...who, address: shown }} />
          </BrowserFrame>
        </PreviewFit>
      </div>
    </div>
  );
}

function CopyShown({ value }: { value: string }) {
  const copy = useCopy();
  return (
    <Button size="s" icon="copy" onClick={() => copy(value)}>
      Copy
    </Button>
  );
}

// --- An address the business owns: shared by setup and Settings ---

export function useOwnDomainSwitch(data: SettingsResponse, run: Run, freeHost: string) {
  const d = data.domain;
  const has = d.custom_domain_status !== "none";
  const [open, setOpen] = useState(has);
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    if (has) setOpen(true);
  }, [has]);
  const flip = () => {
    if (!open) return setOpen(true);
    if (has) return setAsking(true);
    setOpen(false);
  };
  const remove = async () => {
    setAsking(false);
    if (await run(() => api.settings.removeDomain(), `${freeHost} is your address again`)) setOpen(false);
  };
  return { open, asking, setAsking, flip, remove };
}

/** The card with the switch, in setup (a toggle card) and in Settings (a card of its own). */
export function OwnDomainCard({ data, run, freeHost, settings = false }: { data: SettingsResponse; run: Run; freeHost: string; settings?: boolean }) {
  const email = useAuth().user?.email;
  const d = data.domain;
  const { open, asking, setAsking, flip, remove } = useOwnDomainSwitch(data, run, freeHost);
  const confirm = (
    <Sheet
      open={asking}
      onClose={() => setAsking(false)}
      title={`Stop using ${d.custom_domain ?? "it"}?`}
      footer={
        <>
          <Button onClick={() => setAsking(false)}>Cancel</Button>
          <Button variant="p" onClick={() => void remove()}>
            Stop using it
          </Button>
        </>
      }
    >
      <p>{freeHost} turns back on, and links on {d.custom_domain} stop working.</p>
    </Sheet>
  );

  if (settings) {
    return (
      <section className="card ssec">
        <div className="card-h">
          <div>
            <h2>Use an address you own</h2>
            <div className="sub">Like {ownExample(data, email)}</div>
          </div>
          <Toggle on={open} onChange={flip} label="Use an address you own" />
        </div>
        {open ? (
          <div className="card-b">
            <OwnDomainBlock data={data} run={run} freeHost={freeHost} />
          </div>
        ) : null}
        {confirm}
      </section>
    );
  }
  return (
    <div className={cx("tcard", open && "on")}>
      <button type="button" className="th2" style={{ width: "100%", background: "none", border: 0, textAlign: "left" }} aria-expanded={open} onClick={flip}>
        <div className="lgx" style={{ background: "var(--surface-3)", color: "var(--ink-2)" }}>
          <Icon name="globe" />
        </div>
        <div className="sp">
          <div className="b7">Use an address you own too</div>
          <div className="small muted">Like {ownExample(data, email)}</div>
        </div>
        <span className={cx("toggle", open && "on")} aria-hidden="true" />
      </button>
      {open ? <OwnDomainBlock data={data} run={run} freeHost={freeHost} /> : null}
      {confirm}
    </div>
  );
}

/** The address the business owns as typed: its one record, whether it is connected, and Verify. */
export function useOwnDomain(data: SettingsResponse, run: Run) {
  const toast = useToast();
  const d = data.domain;
  const [input, setInput] = useState(d.custom_domain ?? "");
  const [checking, setChecking] = useState(false);
  const typed = cleanDomain(input);
  const current = d.custom_domain && typed === d.custom_domain;
  const state: RecState = current && d.custom_domain_status === "verified" ? "found" : checking ? "checking" : "wait";
  const name = current && d.cname_name ? d.cname_name : typed.split(".")[0] ?? "";
  const root = typed ? rootOf(typed) : "your domain";
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const verify = async () => {
    if (!isDomain(typed)) {
      toast({ text: "That doesn’t look like a web address. Try rooms.yourbusiness.com.", icon: "info" });
      return;
    }
    setChecking(true);
    const added = current || (await run(() => api.settings.addDomain({ domain: typed })));
    if (added) {
      await run(
        () => api.settings.verifyDomain(),
        (r) => (r.domain.custom_domain_status === "verified" ? `${typed} is connected` : r.domain.verify_error ?? "Not connected yet. This usually takes minutes, sometimes up to an hour."),
      );
    }
    if (mounted.current) setChecking(false);
  };
  return { input, setInput, typed, checking, state, name, root, verify, cname: d.cname_value };
}

/**
 * The address the business owns, its one CNAME record, and Verify. Once it connects it takes over every link and the
 * free address turns off; the server only marks it connected while it still holds the address that was checked
 * (specs/formal/domain-verify).
 */
function OwnDomainBlock({ data, run, freeHost }: { data: SettingsResponse; run: Run; freeHost: string }) {
  const email = useAuth().user?.email;
  const { input, setInput, typed, checking, state, name, root, verify, cname } = useOwnDomain(data, run);
  return (
    <div className="tb">
      <div className="field">
        <label htmlFor="cd">Your own address</label>
        <input dir="auto"
          id="cd"
          className="inp"
          value={input}
          placeholder={ownExample(data, email)}
          spellCheck={false}
          autoCapitalize="none"
          inputMode="url"
          onChange={(e) => setInput(e.target.value)}
        />
      </div>
      <div className="small ink2">Add this record to {root}</div>
      <div className={cx("rec", state === "found" && "found")} style={{ gridTemplateColumns: "1fr auto" }}>
        <div className="rh">
          <code className="ty">CNAME</code>
        </div>
        <RecStatus state={state} found="Connected" />
        <div className="vals">
          <CopyValue label="Name" value={name} title="Copy the name" />
          <CopyValue label="Value" value={cname} title="Copy the value" />
        </div>
      </div>
      <div className="row g8 wrap">
        {state === "found" ? (
          <span className="small ink2">Connected. {freeHost} is off.</span>
        ) : (
          <>
            <Button size="s" icon="refresh" disabled={checking || !typed} onClick={() => void verify()}>
              Verify
            </Button>
            <span className="small muted">When it connects, {freeHost} turns off.</span>
          </>
        )}
      </div>
    </div>
  );
}

/** Which room the bare address opens: the newest, or one picked. Saved as it is chosen. */
export function useOpens(data: SettingsResponse, run: Run) {
  const d = data.domain;
  const published = d.published_webinars;
  const chosen = published.find((w) => w.id === d.main_webinar_id) ?? null;
  const [picking, setPicking] = useState(!!chosen);
  const pick = (id: string | null) => void run(() => api.settings.setMainWebinar(id), "Saved");
  const chooseNewest = () => {
    if (!picking) return;
    setPicking(false);
    if (d.main_webinar_id) pick(null);
  };
  return { published, chosen, picking, setPicking, pick, chooseNewest };
}

export function OpensChoice({ data, run }: { data: SettingsResponse; run: Run }) {
  const { published, chosen, picking, setPicking, pick, chooseNewest } = useOpens(data, run);
  return (
    <>
      <button
        type="button"
        className={cx("radio", !picking && "on")}
        aria-pressed={!picking}
        onClick={chooseNewest}
      >
        <span className="rd" />
        <div>
          <div className="b7">Your newest room</div>
          <div className="small muted">{published[0] ? `Now ${published[0].title}` : "None yet"}</div>
        </div>
      </button>
      {published.length ? (
        <div className={cx("radio", picking && "on")}>
          <button type="button" className="rd" aria-label="A room you pick" aria-pressed={picking} onClick={() => setPicking(true)} />
          <div className="sp">
            <button type="button" className="b7" style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }} onClick={() => setPicking(true)}>
              A room you pick
            </button>
            {picking ? (
              <div className="mt8">
                <Select
                  ariaLabel="The room your address opens"
                  value={chosen?.id ?? ""}
                  placeholder="Pick a room"
                  onChange={(v) => pick(v || null)}
                  options={published.map((w) => ({ value: w.id, label: w.title }))}
                />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
